import React, { useCallback, useState } from 'react';
import { Popover } from 'antd';
import { WorkOrderFilterMode } from '../../../../enums/workOrderFilter';
import type { WorkOrderFilterRule } from '../../../../types/InfrastructureManagerTypes';
import { NO_FILTERS, POPOVER_OVERLAP_ALIGN } from '../constants';
import FilterAppliedPrompt from './FilterAppliedPrompt';
import FilterPanel from './FilterPanel';
import styles from './WorkOrderFilter.module.scss';


interface WorkOrderFilterContentProps {
  filters: WorkOrderFilterRule[];
  onFiltersChange: (filters: WorkOrderFilterRule[]) => void;
  onClose: () => void;
}

// Mounts on open and seeds from the applied filters; mode stays local so the fade-out never flips views.
const WorkOrderFilterContent = ({ filters, onFiltersChange, onClose }: WorkOrderFilterContentProps) => {
  const [mode, setMode] = useState(() => (filters.length ? WorkOrderFilterMode.PROMPT : WorkOrderFilterMode.PANEL));

  const handleAdjustFilter = () => setMode(WorkOrderFilterMode.PANEL);
  const handleCancelFilter = () => {
    onFiltersChange(NO_FILTERS);
    onClose();
  };
  const handleApply = (rules: WorkOrderFilterRule[]) => {
    onFiltersChange(rules.length ? rules : NO_FILTERS);
    onClose();
  };

  return mode === WorkOrderFilterMode.PROMPT ? (
    <FilterAppliedPrompt onCancelFilter={handleCancelFilter} onAdjustFilter={handleAdjustFilter} onClose={onClose} />
  ) : (
    <FilterPanel initialRules={filters} onApply={handleApply} onClose={onClose} />
  );
};

interface WorkOrderFilterProps {
  filters: WorkOrderFilterRule[];
  onFiltersChange: (filters: WorkOrderFilterRule[]) => void;
  children: React.ReactElement;
}

const WorkOrderFilter = ({ filters, onFiltersChange, children }: WorkOrderFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const handleClose = useCallback(() => setIsOpen(false), []);

  return (
    <Popover
      open={isOpen}
      onOpenChange={setIsOpen}
      trigger="click"
      placement="bottomLeft"
      arrow={false}
      align={POPOVER_OVERLAP_ALIGN}
      overlayClassName={styles.workOrderFilterOverlay}
      destroyTooltipOnHide
      content={<WorkOrderFilterContent filters={filters} onFiltersChange={onFiltersChange} onClose={handleClose} />}
    >
      {children}
    </Popover>
  );
};

export default WorkOrderFilter;
